import type { SessionViewerResult } from '../auth/api';

const STATE_KEY = 'toshoNavigation';
const VERSION = 2;
const names = ['section', 'hosting-view', 'client-view'] as const;
type Sections = Readonly<Record<string, string>>;
interface NavigationEntry {
  readonly version: number;
  readonly scope: string;
  readonly url: string;
  readonly sections: Sections;
}
export interface NavigationSnapshot {
  readonly sections: Sections;
  readonly visit: number;
}
export function navigationScope(viewer: SessionViewerResult): string {
  return JSON.stringify([
    viewer.userId,
    viewer.accountKind,
    viewer.organization?.id ?? null,
    viewer.organization?.role ?? null,
  ]);
}
function currentUrl(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}
function entry(state: unknown, scope: string): NavigationEntry | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = (state as Record<string, unknown>)[STATE_KEY] as NavigationEntry | undefined;
  if (
    !candidate ||
    candidate.version !== VERSION ||
    candidate.scope !== scope ||
    candidate.url !== currentUrl() ||
    typeof candidate.sections !== 'object' ||
    candidate.sections === null ||
    Array.isArray(candidate.sections) ||
    Object.entries(candidate.sections).some(
      ([name, value]) =>
        !names.includes(name as (typeof names)[number]) ||
        typeof value !== 'string' ||
        value.length > 64,
    )
  )
    return null;
  return candidate;
}
/** History describes presentation only. It never proves a session or a right. */
export function isAuthenticatedNavigation(state: unknown, viewer: SessionViewerResult): boolean {
  return (
    window.location.pathname === '/' &&
    window.location.hash !== '#google-drive' &&
    new URLSearchParams(window.location.search).get('section') !== 'freebe' &&
    entry(state, navigationScope(viewer)) !== null
  );
}
/** Parent and child share one snapshot. Initial registration replaces the entry;
 * explicit screen changes push one. Pop never pushes, preserving Forward.
 */
export class NavigationHistory {
  private snapshot: NavigationSnapshot;
  private readonly listeners = new Set<() => void>();
  public constructor(private readonly scope: string) {
    this.snapshot = {
      sections:
        typeof window === 'undefined' ? {} : (entry(window.history.state, scope)?.sections ?? {}),
      visit: 0,
    };
  }
  public readonly getSnapshot = (): NavigationSnapshot => this.snapshot;
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  public readonly restore = (event: PopStateEvent): void => {
    const target = entry(event.state, this.scope);
    if (!target) return; // App handles auth routes and unknown/foreign entries.
    this.snapshot = { sections: target.sections, visit: this.snapshot.visit + 1 };
    this.notify();
  };
  public initialize(
    name: string,
    value: string,
    allowed: readonly string[],
    preferred: boolean,
  ): void {
    const current = this.snapshot.sections[name];
    if (preferred || current === undefined || !allowed.includes(current))
      this.change(name, value, false);
    else this.write(false);
  }
  public change(name: string, value: string, push = true): void {
    if (!names.includes(name as (typeof names)[number])) return;
    if (this.snapshot.sections[name] === value) {
      if (!push) this.write(false);
      return;
    }
    this.snapshot = { ...this.snapshot, sections: { ...this.snapshot.sections, [name]: value } };
    this.write(push);
    this.notify();
  }
  public synchronizeLocation(): void {
    this.write(false);
  }
  private write(push: boolean): void {
    const state: NavigationEntry = {
      version: VERSION,
      scope: this.scope,
      url: currentUrl(),
      sections: this.snapshot.sections,
    };
    try {
      const previous: unknown = window.history.state;
      const next = {
        ...(typeof previous === 'object' && previous !== null ? previous : {}),
        [STATE_KEY]: state,
      };
      // No fragment or fake popstate: authentication and OAuth keep their semantics.
      if (push) window.history.pushState(next, '');
      else window.history.replaceState(next, '');
    } catch {
      // Privacy/sandbox policies may refuse history. Navigation remains usable;
      // no authentication state is synthesized as a fallback.
    }
  }
  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}
